import React from 'react';
import Prompts from 'context/prompts';
import { getApolloClient } from 'lib/initApollo';
import { toast } from 'components/Toaster';
import { registerDebugCommand } from './commandRegistry';
import { DebugFlags, isDebugFlagEnabled, toggleDebugFlag } from './debugFlag';
import {
  DebugDocument,
  DebugMutation,
  DebugMutationVariables,
} from './operation.generated';
import { DebugKey } from '../../../server/graphql/__generated.types';

let registered = false;

/**
 * Commands are registered once, but the palette can remount under a different
 * provider, so read the dispatch through a mutable binding rather than
 * capturing the one that happened to register first.
 */
let openPrompt: React.Dispatch<Prompts.ContextActions> = () => {};

/**
 * Last known server value of the section error simulation; null until the
 * first toggle this session, as the switch lives in redis.
 */
let sectionErrorSimulationEnabled: boolean | null = null;

/**
 * `/dashboard/studio/[projectId]` — re-checked when the palette opens.
 * @note consider passing router via `registerDefaultDebugCommands` if we ever
 * do this again...
 * */
function isOnStudioProjectDetailPage(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return /^\/dashboard\/studio\/[^/]+\/?$/.test(window.location.pathname);
}

/**
 * Built-in Command-K actions for the debug palette.
 * Idempotent — safe to call from multiple mounts.
 */
export default function registerDefaultDebugCommands(
  promptsDispatch: React.Dispatch<Prompts.ContextActions>
) {
  openPrompt = promptsDispatch;
  if (registered) {
    return;
  }
  registered = true;

  const importCsvCommand = (
    id: string,
    label: string,
    kind: Prompts.OpenImportCsvPrompt['payload']['kind'],
    keywords: string[],
    isAvailable?: () => boolean
  ) =>
    registerDebugCommand({
      id,
      label,
      keywords: ['csv', 'import', 'bulk', 'seed', ...keywords],
      isAvailable,
      perform: () => {
        openPrompt({
          type: 'OpenImportCsvPrompt',
          payload: { kind },
        });
      },
    });

  importCsvCommand(
    'import-sections-csv',
    'Import sections from CSV',
    'sections',
    ['sections', 'script', 'project'],
    isOnStudioProjectDetailPage
  );
  importCsvCommand(
    'import-personal-replacements-csv',
    'Import personal replacements from CSV',
    'personalReplacements',
    ['replacements', 'personal', 'phonetic', 'respelling', 'library']
  );
  importCsvCommand(
    'import-team-replacements-csv',
    'Import team replacements from CSV',
    'teamReplacements',
    ['replacements', 'team', 'phonetic', 'respelling', 'library']
  );

  registerDebugCommand({
    id: 'toggle-data-variant',
    label: () =>
      isDebugFlagEnabled(DebugFlags.ButtonVariant)
        ? 'Hide data-variant on controls'
        : 'Show data-variant on controls',
    keywords: ['button', 'variant', 'highlight', 'badge', 'toggle', 'show'],
    perform: () => {
      toggleDebugFlag(DebugFlags.ButtonVariant);
    },
  });

  registerDebugCommand({
    id: 'toggle-section-debug',
    label: () =>
      isDebugFlagEnabled(DebugFlags.SectionDebug)
        ? 'Hide section debug'
        : 'Show section debug',
    keywords: [
      'universal',
      'indicator',
      'avatar',
      'variant',
      'debugger',
      'hide',
      'show',
    ],
    perform: () => {
      toggleDebugFlag(DebugFlags.SectionDebug);
    },
  });

  registerDebugCommand({
    id: 'toggle-section-error-simulation',
    label: () => {
      if (sectionErrorSimulationEnabled === null) {
        return 'Toggle section error simulation';
      }
      return sectionErrorSimulationEnabled
        ? 'Disable section error simulation'
        : 'Enable section error simulation';
    },
    keywords: ['section', 'error', 'simulate', 'save', 'fail', 'redis'],
    perform: async () => {
      const client = getApolloClient();
      if (!client) {
        // eslint-disable-next-line no-console
        console.warn('[debug] Apollo client is not initialized');
        return;
      }
      try {
        const { data } = await client.mutate<
          DebugMutation,
          DebugMutationVariables
        >({
          mutation: DebugDocument,
          // no `enabled` toggles the server value
          variables: { inputs: { key: DebugKey.SectionError } },
        });
        if (!data?.debug.success) {
          const error = new Error(
            data?.debug.message ?? 'Failed to toggle section error simulation'
          );
          throw error;
        }
        sectionErrorSimulationEnabled = data.debug.enabled ?? false;
        toast({
          message: `Section error simulation ${
            sectionErrorSimulationEnabled ? 'enabled' : 'disabled'
          }`,
          type: 'success',
        });
      } catch (error) {
        toast({
          message:
            error instanceof Error && error.message
              ? error.message
              : 'Failed to toggle section error simulation',
          type: 'error',
        });
      }
    },
  });

  registerDebugCommand({
    id: 'clear-apollo-cache',
    label: 'Clear Apollo cache',
    keywords: ['graphql', 'store', 'reset', 'client'],
    perform: async () => {
      const client = getApolloClient();
      if (!client) {
        // eslint-disable-next-line no-console
        console.warn('[debug] Apollo client is not initialized');
        return;
      }
      await client.cache.reset();
      // eslint-disable-next-line no-console
      console.info('[debug] Apollo cache reset');
    },
  });
}
